import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'info' | 'warning' | 'error' | 'success';
  module: string;
  action: string;
  message: string;
  userId?: string;
  userName?: string;
  ip?: string;
  details?: any;
}

// Mock data
const mockLogs: LogEntry[] = [
  {
    id: '1',
    timestamp: '2026-01-08 14:32:15',
    level: 'success',
    module: 'Auth',
    action: 'LOGIN',
    message: 'Utilizador autenticado com sucesso',
    userId: 'USR001',
    userName: 'admin@pagali.cv',
    ip: '192.168.1.100',
    details: { browser: 'Chrome 120', os: 'Windows 11' },
  },
  {
    id: '2',
    timestamp: '2026-01-08 14:30:45',
    level: 'info',
    module: 'Pagamentos',
    action: 'PAYMENT_CREATED',
    message: 'Novo pagamento registado: REF123456',
    userId: 'USR001',
    userName: 'admin@pagali.cv',
    ip: '192.168.1.100',
    details: { reference: 'REF123456', amount: 15000, entityId: 'ENT001' },
  },
  {
    id: '3',
    timestamp: '2026-01-08 14:28:30',
    level: 'warning',
    module: 'SISP',
    action: 'TIMEOUT',
    message: 'Timeout na comunicação com SISP - tentativa 2/3',
    details: { endpoint: '/api/sisp/validate', timeout: 30000 },
  },
  {
    id: '4',
    timestamp: '2026-01-08 14:25:00',
    level: 'error',
    module: 'API',
    action: 'REQUEST_FAILED',
    message: 'Erro ao processar pedido: Connection refused',
    ip: '10.0.0.50',
    details: { endpoint: '/api/external/bank', statusCode: 503, error: 'ECONNREFUSED' },
  },
  {
    id: '5',
    timestamp: '2026-01-08 14:20:00',
    level: 'success',
    module: 'Faturação',
    action: 'INVOICE_PAID',
    message: 'Fatura FAT2026/000123 paga totalmente',
    userId: 'USR002',
    userName: 'operador@cvtel.cv',
    ip: '192.168.1.101',
    details: { invoiceNumber: 'FAT2026/000123', amount: 45000, paymentMethod: 'TRANSFER' },
  },
  {
    id: '6',
    timestamp: '2026-01-08 14:15:00',
    level: 'info',
    module: 'Gestão',
    action: 'USER_UPDATED',
    message: 'Dados de utilizador actualizados',
    userId: 'USR001',
    userName: 'admin@pagali.cv',
    ip: '192.168.1.100',
    details: { targetUser: 'USR003', fields: ['nome', 'telefone'] },
  },
  {
    id: '7',
    timestamp: '2026-01-08 14:10:00',
    level: 'warning',
    module: 'Sistema',
    action: 'HIGH_MEMORY',
    message: 'Utilização de memória acima de 80%',
    details: { memoryUsage: 82, threshold: 80 },
  },
  {
    id: '8',
    timestamp: '2026-01-08 14:05:00',
    level: 'success',
    module: 'Auth',
    action: 'PASSWORD_RESET',
    message: 'Password redefinida com sucesso',
    userId: 'USR004',
    userName: 'cliente@empresa.cv',
    ip: '192.168.2.50',
  },
  {
    id: '9',
    timestamp: '2026-01-08 14:00:00',
    level: 'error',
    module: 'Notificações',
    action: 'EMAIL_FAILED',
    message: 'Falha ao enviar email de notificação',
    details: { recipient: 'user@example.cv', error: 'Invalid email address' },
  },
  {
    id: '10',
    timestamp: '2026-01-08 13:55:00',
    level: 'info',
    module: 'Pagamentos',
    action: 'PAYMENT_CANCELLED',
    message: 'Pagamento REF789012 cancelado pelo utilizador',
    userId: 'USR005',
    userName: 'cliente2@empresa.cv',
    ip: '192.168.3.10',
    details: { reference: 'REF789012', reason: 'Duplicado' },
  },
];

const Logs: React.FC = () => {
  const { t } = useTranslation();
  const [logs] = useState<LogEntry[]>(mockLogs);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'warning':
        return <WarningIcon color="warning" fontSize="small" />;
      case 'success':
        return <SuccessIcon color="success" fontSize="small" />;
      default:
        return <InfoIcon color="info" fontSize="small" />;
    }
  };

  const getLevelChip = (level: string) => {
    const config: Record<string, { color: 'error' | 'warning' | 'success' | 'info'; label: string }> = {
      error: { color: 'error', label: t('configLogs.levelError') },
      warning: { color: 'warning', label: t('configLogs.levelWarning') },
      success: { color: 'success', label: t('configLogs.levelSuccess') },
      info: { color: 'info', label: t('configLogs.levelInfo') },
    };
    const { color, label } = config[level] || config.info;
    return <Chip size="small" color={color} label={label} icon={getLevelIcon(level)} />;
  };

  const modules = ['all', ...Array.from(new Set(logs.map(l => l.module)))];

  const filteredLogs = logs.filter(log => {
    const matchesSearch = searchTerm === '' ||
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.module.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.userName && log.userName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesLevel = levelFilter === 'all' || log.level === levelFilter;
    const matchesModule = moduleFilter === 'all' || log.module === moduleFilter;
    return matchesSearch && matchesLevel && matchesModule;
  });

  const stats = {
    total: logs.length,
    errors: logs.filter(l => l.level === 'error').length,
    warnings: logs.filter(l => l.level === 'warning').length,
    success: logs.filter(l => l.level === 'success').length,
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('configLogs.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />}>
            {t('common.refresh')}
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />}>
            {t('common.export')}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('configLogs.totalToday')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" />
                <Box>
                  <Typography variant="h5" fontWeight={700} color="error.main">{stats.errors}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('configLogs.errors')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" />
                <Box>
                  <Typography variant="h5" fontWeight={700} color="warning.main">{stats.warnings}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('configLogs.warnings')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SuccessIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700} color="success.main">{stats.success}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('common.success')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('configLogs.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('configLogs.level')}
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="error">{t('configLogs.errors')}</MenuItem>
                <MenuItem value="warning">{t('configLogs.warnings')}</MenuItem>
                <MenuItem value="success">{t('common.success')}</MenuItem>
                <MenuItem value="info">{t('configLogs.levelInfo')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('configLogs.module')}
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
              >
                {modules.map(m => (
                  <MenuItem key={m} value={m}>
                    {m === 'all' ? t('common.all') : m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setLevelFilter('all');
                  setModuleFilter('all');
                }}
              >
                {t('configLogs.clear')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={160}>{t('configLogs.dateTime')}</TableCell>
                <TableCell width={100}>{t('configLogs.level')}</TableCell>
                <TableCell width={120}>{t('configLogs.module')}</TableCell>
                <TableCell width={150}>{t('configLogs.action')}</TableCell>
                <TableCell>{t('configLogs.message')}</TableCell>
                <TableCell width={150}>{t('configLogs.user')}</TableCell>
                <TableCell width={60} align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {log.timestamp}
                    </Typography>
                  </TableCell>
                  <TableCell>{getLevelChip(log.level)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {log.module}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={log.action} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                      {log.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {log.userName ? (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {log.userName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => setSelectedLog(log)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredLogs.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
        />
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onClose={() => setSelectedLog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          {t('configLogs.logDetails')}
        </DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('configLogs.dateTime')}</Typography>
                  <Typography variant="body2" fontFamily="monospace">{selectedLog.timestamp}</Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('configLogs.level')}</Typography>
                  <Box>{getLevelChip(selectedLog.level)}</Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('configLogs.module')}</Typography>
                  <Typography variant="body2" fontWeight={500}>{selectedLog.module}</Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('configLogs.action')}</Typography>
                  <Box><Chip label={selectedLog.action} size="small" variant="outlined" /></Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary">{t('configLogs.message')}</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{selectedLog.message}</Typography>

              {selectedLog.userName && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">{t('configLogs.user')}</Typography>
                  <Typography variant="body2">
                    {selectedLog.userName} ({selectedLog.userId})
                  </Typography>
                </Box>
              )}

              {selectedLog.ip && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">{t('configLogs.ipAddress')}</Typography>
                  <Typography variant="body2" fontFamily="monospace">{selectedLog.ip}</Typography>
                </Box>
              )}

              {selectedLog.details && (
                <>
                  <Typography variant="caption" color="text.secondary">{t('configLogs.additionalDetails')}</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'action.hover' }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Logs;
